import {useMemo} from 'react'
import {useProduct, useProducts} from '@salesforce/commerce-sdk-react'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {DEFAULT_GET_PRODUCT_PRICE_PARAMETERS} from './useProductDetail'
import {getProductsToAddToCart, isProductSet} from '../utils/product-utils'
import {useProductSetFullPrices, useProductSetPrice} from './useScapiCustomApi'

/**
 * Retrieve product name given productId
 * @param {string} productId - Product id to retrieve the name of
 * @param {import("@tanstack/react-query").UseQueryOptions} [queryOptions] - Options to configure the behavior of the query
 * @returns {import("@tanstack/react-query").UseQueryResult<{name: string}>} Query result where data contains an object with a single property `name` of type string
 */
export const useProductName = (productId, queryOptions) =>
    useProduct(
        {
            parameters: {
                id: productId,
                select: '(name)'
            }
        },
        {
            ...getConfig().app.queryCache?.static,
            ...queryOptions,
            enabled: Boolean(productId) && queryOptions?.enabled !== false
        }
    )

/**
 * Fetch specific product price based on currently selected set products and variants.
 *
 * @param {object} product - Product to retrieve pricing data for - must have the isSetProductSelected attributes
 * @param {import("@tanstack/react-query").UseQueryOptions} [queryOptions] - Options to configure the behavior of the query
 * @returns {import("@tanstack/react-query").UseQueryResult<{
 *     c_taxRate: number,
 *     productPromotions: Array,
 *     price: number,
 *     setAccessories: Array<{
 *       id: string,
 *       c_taxRate: number,
 *       productPromotions: Array,
 *       price: number
 *     }>,
 *     setExtensions: Array<{
 *       id: string,
 *       c_taxRate: number,
 *       productPromotions: Array,
 *       price: number
 *     }>,
 *     setProducts: Array<{
 *       id: string,
 *       c_taxRate: number,
 *       productPromotions: Array,
 *       price: number
 *     }>,
 *   }>} Query result where data contains the pricing information for the product
 */
export const useProductPrice = (product, queryOptions) => {
    const productIsSet = useMemo(() => isProductSet(product), [product])

    const setProducts = useMemo(
        () =>
            productIsSet
                ? [
                      ...(product?.setProducts || []),
                      ...(product?.setExtensions || []),
                      ...(product?.setAccessories || [])
                  ].filter(Boolean)
                : [],
        [product, productIsSet]
    )

    const selectedSetProducts = useMemo(
        () =>
            productIsSet
                ? [
                      ...getProductsToAddToCart(product),
                      // engraving is added automatically by sfcc when engaving custom attribute is added when adding engravable product to cart
                      // so we need to manually simulate that here
                      ...(product?.engraving?.isSetProductSelected ? [product?.engraving] : [])
                  ]
                : [],
        [product, productIsSet]
    )
    const unselectedSetProducts = useMemo(() => {
        const selectedSet = new Set(selectedSetProducts.map((p) => p.id))
        return setProducts.filter((p) => !selectedSet.has(p.id))
    }, [setProducts, selectedSetProducts])

    const priceParameters = useMemo(() => {
        const c_selected = selectedSetProducts
            .map((p) => p.id)
            .sort((a, b) => a.localeCompare(b))
            .join('|')
        if (!c_selected.length) return
        const c_unselected = unselectedSetProducts
            .map((p) => p.id)
            .sort((a, b) => a.localeCompare(b))
            .join('|')
        return {
            c_selected,
            ...(c_unselected.length > 0 && {c_unselected})
        }
    }, [selectedSetProducts, unselectedSetProducts])

    const productPriceQueryResult = useProductPreliminaryPrice(product, {enabled: !productIsSet})

    const setProductPriceQueryResult = useProductSetFullPrices(
        {
            parameters: priceParameters
        },
        {
            ...getConfig().app.queryCache?.availabilityPrice,
            keepPreviousData: false,
            ...queryOptions,
            enabled: Boolean(productIsSet && priceParameters) && queryOptions?.enabled !== false
        }
    )

    const setProductPriceData = useMemo(() => {
        // convert the set product's price and promotions
        const convert = (setProduct) => {
            // use the correct list
            const list = selectedSetProducts.some((p) => p.id === setProduct.id)
                ? setProductPriceQueryResult.data?.selected?.productLineItems
                : setProductPriceQueryResult.data?.unselected
            // find the product line for the set product - look for the variant id
            const productLine = list?.find((p) => p.variantId === setProduct.id)

            if (!productLine) {
                return null
            }

            // convert the set product price
            return {
                c_taxRate:
                    productLine.c_taxRate ??
                    setProductPriceQueryResult.data?.selected?.c_taxRate ??
                    setProduct.c_taxRate,
                id: setProduct.id,
                price: productLine.price,
                productPromotions: [
                    // add promotional price for the set product
                    ...(productLine?.adjustedPrice != null
                        ? [
                              {
                                  promotionalPrice: productLine?.adjustedPrice
                              }
                          ]
                        : []),
                    // convert the regular promotions
                    ...(productLine.pliPromotions || productLine.promotions || []).map(
                        (promotionId) => ({
                            promotionId
                        })
                    )
                ]
            }
        }

        // convert the price and promotions of the product set
        return {
            c_taxRate: setProductPriceQueryResult.data?.selected?.c_taxRate ?? product?.c_taxRate,
            price: setProductPriceQueryResult.data?.selected?.price ?? product?.price,
            productPromotions:
                setProductPriceQueryResult.data?.selected?.productPromotions ??
                product?.productPromotions,
            setProducts: product?.setProducts?.map(convert).filter(Boolean),
            setExtensions: product?.setExtensions?.map(convert).filter(Boolean),
            setAccessories: product?.setAccessories?.map(convert).filter(Boolean),
            engraving: product?.engraving && convert(product?.engraving)
        }
    }, [product, setProductPriceQueryResult.data, selectedSetProducts])

    if (productIsSet) {
        return {
            ...setProductPriceQueryResult,
            data: setProductPriceData
        }
    }

    return productPriceQueryResult
}

/**
 * Fetch product price before configuring any variant or set products
 * @param {Object} params - Parameters object
 * @param {string} params.id - Product id to retrieve pricing data for
 * @param {Object} [params.type] - SFCC product type structure (usually has a single property with value true). When not provided, loads for both product set and non-set, which is slower.
 * @param {import("@tanstack/react-query").UseQueryOptions} [queryOptions] - Options to configure the behavior of the query
 * @returns {{
 *   data: {
 *     c_taxRate: number,
 *     productPromotions: Array,
 *     price: number
 *   },
 *   error: Error,
 *   isError: boolean,
 *   isFetching: boolean,
 *   isLoading: boolean,
 *   isInitialLoading: boolean
 * }}
 */
export const useProductPreliminaryPrice = ({id, type}, queryOptions) => {
    // load price data for product
    const productQuery = useProduct(
        {
            parameters: {
                ...DEFAULT_GET_PRODUCT_PRICE_PARAMETERS,
                id
            }
        },
        {
            ...getConfig().app.queryCache?.availabilityPrice,
            ...queryOptions,
            // fetch only for product
            enabled:
                Boolean(id && (!type || !isProductSet({type}))) && queryOptions?.enabled !== false
        }
    )
    // load price data for product set
    const productSetQuery = useProductSetPrice(
        {
            parameters: {
                c_pid: id
            }
        },
        {
            ...getConfig().app.queryCache?.availabilityPrice,
            ...queryOptions,
            // fetch only for product set
            enabled:
                Boolean(id && (!type || isProductSet({type}))) && queryOptions?.enabled !== false
        }
    )

    const productData = useMemo(
        () => ({
            c_taxRate: productSetQuery.data?.resp?.c_taxRate ?? productQuery.data?.c_taxRate,
            productPromotions:
                productSetQuery.data?.resp?.productPromotions ??
                productQuery.data?.productPromotions,
            price: productSetQuery.data?.resp?.price ?? productQuery.data?.price
        }),
        [productQuery.data, productSetQuery.data]
    )

    return {
        ...(!type
            ? {
                  error: productQuery.error || productSetQuery.error,
                  isError: productQuery.isError || productSetQuery.isError,
                  isFetching: productQuery.isFetching || productSetQuery.isFetching,
                  isInitialLoading:
                      productQuery.isInitialLoading || productSetQuery.isInitialLoading,
                  isLoading: productQuery.isLoading || productSetQuery.isLoading
              }
            : isProductSet({type})
            ? {
                  error: productSetQuery.error,
                  isError: productSetQuery.isError,
                  isFetching: productSetQuery.isFetching,
                  isInitialLoading: productSetQuery.isInitialLoading,
                  isLoading: productSetQuery.isLoading
              }
            : {
                  error: productQuery.error,
                  isError: productQuery.isError,
                  isFetching: productQuery.isFetching,
                  isInitialLoading: productQuery.isInitialLoading,
                  isLoading: productQuery.isLoading
              }),
        data: productData
    }
}

/**
 * Fetch orderable status per variant for a list of master product IDs.
 * Returns a flat map of variantProductId → orderable boolean.
 *
 * @param {string[]} masterIds - Master product IDs to fetch variant orderable status for
 * @param {import("@tanstack/react-query").UseQueryOptions} [queryOptions]
 * @returns {import("@tanstack/react-query").UseQueryResult<Map<string, boolean>>} map from variant id to orderable flag
 */
export const useProductVariantsOrderable = (masterIds, queryOptions) => {
    const query = useProducts(
        {
            parameters: {
                ids: masterIds?.join(','),
                expand: ['availability', 'variations'],
                select: '(data.(variants.(*)))'
            }
        },
        {
            ...getConfig().app.queryCache?.availabilityPrice,
            ...queryOptions,
            enabled: masterIds?.length > 0 && queryOptions?.enabled !== false
        }
    )

    const orderableByProductId = useMemo(
        () =>
            Object.fromEntries(
                (query.data?.data ?? []).flatMap(
                    (product) =>
                        product.variants?.map(({productId, orderable}) => [productId, orderable]) ??
                        []
                )
            ),
        [query.data]
    )

    return {...query, data: orderableByProductId}
}
